import React, { useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import { isEnterWithoutComposing } from '@/common/util/keyboard'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import { useAuth } from '../../AuthContext'
import { CONTRACT_LABELS, RICH_UI_COMPONENTS } from './richUiComponents'
import {
  presetErrorMessage,
  validateBundleForm,
  validateHeroForm,
  validateQuickChoiceForm,
} from './presetValidation'
import ModalOverlay from './ModalOverlay'

// 共通スタイル（SmartHub デザイントークン）
const CARD = 'rounded-xl border border-secondary bg-surface shadow-area'
const INPUT = 'h-8 rounded-lg border border-border-input bg-surface px-2.5 text-size-13 text-primary outline-none hover:border-border-input-hover focus:border-border-input-active placeholder:text-placeholder'
const TEXTAREA = 'w-full resize-y rounded-lg border border-border-input bg-surface px-2.5 py-1.5 text-size-13 text-primary outline-none hover:border-border-input-hover focus:border-border-input-active placeholder:text-placeholder'
const LABEL = 'text-secondary text-[11px] font-medium'
const FIELD = 'flex flex-col gap-1'

// ============================================================
// ⑫ リッチUIデザイナー（統合版）
//   - 共通設定（全体/テーマ/自由CSS/プレビュー）
//   - コンポーネント別設定 × 10
//   - Hero / Bundle / QuickChoice はプリセット管理を内包
// ============================================================

// コンポーネントの定義は richUiComponents.ts を単一ソースにする
// （ダッシュボードのコンポーネント別効果指標と表示名を揃えるため）
const NAV_ITEMS = [
  { kind: 'common', key: 'overview', label: '🎛️ 全体設定' },
  { kind: 'common', key: 'theme', label: '🎨 共通テーマ' },
  { kind: 'common', key: 'css', label: '📝 自由CSS' },
  { kind: 'common', key: 'preview', label: '👁️ プレビュー' },
  ...RICH_UI_COMPONENTS.map(c => ({
    kind: 'component',
    key: c.key,
    label: c.navLabel,
    ...(c.preset ? { preset: c.preset } : {}),
    ...(c.requiresContract ? { requiresContract: c.requiresContract } : {}),
  })),
]

const DEFAULT_THEME = {
  accent_color: '#6366f1',
  bg_color: '#ffffff',
  text_color: '#111111',
  border_radius: '12px',
  font_size: '13px',
}

const COMPONENT_DEFAULTS = {
  product_gallery: { max_items: 6, layout: 'grid', grid_columns: 2, image_aspect_ratio: '1/1', emphasis_first: false, custom_css: '' },
  hero_card: { default_animation_in: 'fade_in', default_background_type: 'gradient', default_cta_hover_effect: 'lift', custom_css: '' },
  comparison_table: { recommended_highlight_color: '#eef2ff', highlight_best_value: true, header_bg_color: '#f9fafb', custom_css: '' },
  comparison_radar: { color_palette: 'default', axis_color: '#e5e7eb', fill_opacity: 25, size: 'medium', custom_css: '' },
  personalized_cards: { reason_box_color: 'rgba(99,102,241,0.06)', show_relevance_score: true, show_segment_label: true, custom_css: '' },
  quick_choice: { default_icon_bg_color: '#fafafa', card_size: 'standard', custom_css: '' },
  filter_slider: { slider_accent_color: '', preview_count: 3, show_view_all: true, custom_css: '' },
  review_highlights: { positive_color: '#198754', concern_color: '#b45309', max_positive: 5, max_concern: 3, show_representative_review: true, custom_css: '' },
  bundle_offer: { default_header_color: '#0d6efd', default_accent_color: '#198754', custom_css: '' },
}

// ============================================================
// メインコンポーネント
// ============================================================

export default function RichUIDesigner() {
  const { siteId } = useParams()
  const { authHeaders } = useAuth()

  const [campaigns, setCampaigns] = useState([])
  // 2026-10-03 修正：編集中の配信を URL（?campaign=）に持たせる（SMART_HUB_DEV-138）。
  // state だけで持っていたため、リロードすると一覧の先頭の配信に切り替わっていた
  // 修正前: const [selectedCampaignId, setSelectedCampaignId] = useState(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedCampaignId = Number(searchParams.get('campaign')) || null
  const setSelectedCampaignId = (cid) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (cid) next.set('campaign', String(cid))
      else next.delete('campaign')
      return next
    }, { replace: true })
  }
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [activeNav, setActiveNav] = useState('overview')

  const [settings, setSettings] = useState({
    rich_ui_enabled: false,
    rich_ui_components_enabled: ['text_only'],
    rich_ui_theme: { ...DEFAULT_THEME },
    rich_ui_custom_css: '',
    rich_ui_component_options: {},
    rich_ui_follow_up_enabled: true,
  })
  const [showHelp, setShowHelp] = useState(false)
  // サイトの契約フラグ。Reco-Media / UGC を契約していないサイトでは、
  // それに依存するコンポーネントの設定を出さない
  const [site, setSite] = useState(null)

  const fetchSite = async () => {
    try {
      const r = await fetch(`/api/sites/${siteId}`, { headers: authHeaders() })
      if (r.ok) setSite(await r.json())
    } catch {
      // 契約フラグが取れなくても画面は開く（設定できないだけ）
    }
  }

  const fetchCampaigns = async () => {
    try {
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/campaigns`, { headers: authHeaders() })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const d = await r.json()
      const list = d.items || []
      setCampaigns(list)
      // URL の配信が無い（未指定・削除済み・別サイトのID）ときだけ先頭を選ぶ
      if (list.length && !list.some(c => c.id === selectedCampaignId)) setSelectedCampaignId(list[0].id)
    } catch (e) {
      setError(e.message)
    }
  }

  const fetchSettings = async (cid) => {
    if (!cid) return
    setLoading(true); setError(null)
    try {
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/campaigns/${cid}/rich-ui`, { headers: authHeaders() })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const d = await r.json()
      setSettings({
        rich_ui_enabled: !!d.rich_ui_enabled,
        rich_ui_components_enabled: d.rich_ui_components_enabled || ['text_only'],
        rich_ui_theme: { ...DEFAULT_THEME, ...(d.rich_ui_theme || {}) },
        rich_ui_custom_css: d.rich_ui_custom_css || '',
        rich_ui_component_options: d.rich_ui_component_options || {},
        rich_ui_follow_up_enabled: d.rich_ui_follow_up_enabled !== false,
      })
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchCampaigns(); fetchSite() }, [siteId])
  useEffect(() => { if (selectedCampaignId) fetchSettings(selectedCampaignId) }, [selectedCampaignId])

  const save = async () => {
    if (!selectedCampaignId) return
    setSaving(true); setError(null)
    try {
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/campaigns/${selectedCampaignId}/rich-ui`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(settings),
      })
      if (!r.ok) {
        const t = await r.text()
        throw new Error(`HTTP ${r.status}: ${t}`)
      }
      const d = await r.json()
      setSettings(prev => ({
        ...prev,
        rich_ui_enabled: !!d.rich_ui_enabled,
        rich_ui_components_enabled: d.rich_ui_components_enabled || ['text_only'],
        rich_ui_component_options: d.rich_ui_component_options || prev.rich_ui_component_options,
      }))
      alert('保存しました')
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  const toggleComponent = (key) => {
    setSettings(prev => {
      const list = prev.rich_ui_components_enabled || []
      const next = list.includes(key) ? list.filter(k => k !== key) : [...list, key]
      if (!next.includes('text_only')) next.unshift('text_only')
      return { ...prev, rich_ui_components_enabled: next }
    })
  }

  const updateTheme = (k, v) => setSettings(prev => ({ ...prev, rich_ui_theme: { ...prev.rich_ui_theme, [k]: v } }))

  const getOptions = (componentKey) => ({
    ...COMPONENT_DEFAULTS[componentKey],
    ...((settings.rich_ui_component_options || {})[componentKey] || {}),
  })

  const updateOptions = (componentKey, patch) => {
    setSettings(prev => ({
      ...prev,
      rich_ui_component_options: {
        ...(prev.rich_ui_component_options || {}),
        [componentKey]: {
          ...COMPONENT_DEFAULTS[componentKey],
          ...((prev.rich_ui_component_options || {})[componentKey] || {}),
          ...patch,
        },
      },
    }))
  }

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='🎨 リッチUIデザイナー'
        description='ウィジェット応答のリッチUIコンポーネントを配信単位で設定します。共通設定とコンポーネント別設定が左ナビから切替できます。'
        rightNode={
          <Button color={ButtonColor.SECONDARY} onClick={() => setShowHelp(true)}>❓ 選択ガイド</Button>
        }
      />
      {showHelp && <HelpOverlay onClose={() => setShowHelp(false)} />}

      {/* キャンペーン選択 + 保存 */}
      <div className={`${CARD} mb-3.5 flex items-center gap-2.5 p-3.5`}>
        <label className='text-secondary text-[12px] font-semibold'>対象配信:</label>
        <select
          value={selectedCampaignId || ''}
          onChange={e => setSelectedCampaignId(Number(e.target.value))}
          className={`${INPUT} min-w-[240px]`}
        >
          {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}（{c.status}）</option>)}
        </select>
        <div className='flex-1' />
        <Button color={ButtonColor.PRIMARY} onClick={save} disabled={saving}>{saving ? '保存中...' : '💾 保存'}</Button>
      </div>

      {error && <div className='bg-surface-critical text-critical mb-3 rounded-lg p-2.5 text-xs'>⚠️ {error}</div>}

      {loading ? <div className='text-secondary py-16 text-center text-sm'>読み込み中...</div>
        : !selectedCampaignId ? <div className='text-secondary py-16 text-center text-sm'>配信が見つかりません。先に配信を作成してください。</div>
        : (
          <div className='flex items-start gap-3.5'>
            {/* 左ナビ */}
            <div className={`${CARD} sticky top-3.5 w-[230px] flex-shrink-0 p-2`}>
              <NavSection title="共通設定" items={NAV_ITEMS.filter(n => n.kind === 'common')} active={activeNav} onSelect={setActiveNav} settings={settings} />
              <div className='h-2' />
              <NavSection title="コンポーネント" items={NAV_ITEMS.filter(n => n.kind === 'component')} active={activeNav} onSelect={setActiveNav} settings={settings} />
            </div>

            {/* 右ペイン */}
            <div className='min-w-0 flex-1'>
              <PaneSwitch
                activeNav={activeNav}
                settings={settings}
                setSettings={setSettings}
                updateTheme={updateTheme}
                toggleComponent={toggleComponent}
                getOptions={getOptions}
                updateOptions={updateOptions}
                siteId={siteId}
                site={site}
              />
            </div>
          </div>
        )}
    </Main>
  )
}

// ============================================================
// 左ナビ
// ============================================================

function NavSection({ title, items, active, onSelect, settings }) {
  return (
    <div>
      <div className='text-tertiary px-2 pt-1.5 pb-1 text-[10px] font-semibold tracking-wide'>{title}</div>
      {items.map(it => {
        const isActive = active === it.key
        const isCompEnabled = it.kind === 'component' ? settings.rich_ui_components_enabled.includes(it.key) : null
        return (
          <button
            key={it.key}
            onClick={() => onSelect(it.key)}
            className={`mb-0.5 flex w-full cursor-pointer items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-left text-[12px] ${
              isActive ? 'bg-fill-brand text-on-bg-fill font-semibold' : 'text-secondary hover:bg-surface-hover'
            }`}
          >
            <span className='flex-1'>{it.label}</span>
            {it.kind === 'component' && (
              <span className={`rounded-md px-1.5 py-px text-[9px] font-semibold ${isCompEnabled ? 'bg-surface-success text-success' : 'bg-surface-secondary text-secondary'}`}>{isCompEnabled ? 'ON' : 'OFF'}</span>
            )}
          </button>
        )
      })}
    </div>
  )
}

// ============================================================
// ペイン切替
// ============================================================

function PaneSwitch({ activeNav, settings, setSettings, updateTheme, toggleComponent, getOptions, updateOptions, siteId, site }) {
  const navItem = NAV_ITEMS.find(n => n.key === activeNav)
  if (!navItem) return null

  if (navItem.kind === 'common') {
    if (activeNav === 'overview') return <OverviewPane settings={settings} setSettings={setSettings} />
    if (activeNav === 'theme') return <ThemePane settings={settings} updateTheme={updateTheme} />
    if (activeNav === 'css') return <CssPane settings={settings} setSettings={setSettings} />
    if (activeNav === 'preview') return <PreviewPane settings={settings} />
  }

  // コンポーネント別ペイン
  return (
    <ComponentPane
      navItem={navItem}
      settings={settings}
      toggleComponent={toggleComponent}
      getOptions={getOptions}
      updateOptions={updateOptions}
      siteId={siteId}
      theme={settings.rich_ui_theme}
      site={site}
    />
  )
}

// ============================================================
// 共通ペイン: 全体設定
// ============================================================

function OverviewPane({ settings, setSettings }) {
  const enabledCount = settings.rich_ui_components_enabled.filter(k => k !== 'text_only').length
  return (
    <div className={`${CARD} p-[18px]`}>
      <h2 className='text-primary mb-3.5 text-base font-bold'>🎛️ 全体設定</h2>
      <label className='text-primary mb-3.5 flex items-center gap-2 text-sm'>
        <input type="checkbox" checked={settings.rich_ui_enabled} onChange={e => setSettings(prev => ({ ...prev, rich_ui_enabled: e.target.checked }))} />
        リッチUI を有効化（OFFの場合は従来のテキスト応答のみ）
      </label>
      <label className='text-primary mb-3.5 flex items-center gap-2 text-sm'>
        <input type="checkbox" checked={settings.rich_ui_follow_up_enabled} onChange={e => setSettings(prev => ({ ...prev, rich_ui_follow_up_enabled: e.target.checked }))} />
        フォローアップチップ（次の質問候補ボタン）を表示
      </label>
      <div className='bg-surface-emphasis text-emphasis mt-4 rounded-lg p-2.5 text-xs leading-relaxed'>
        現在 <strong>{enabledCount} 個</strong> のリッチコンポーネントが有効です。各コンポーネントの ON/OFF と詳細設定は左ナビから切替可能。
      </div>
      <div className='bg-surface-warning text-warning mt-3 rounded-lg p-2.5 text-xs leading-relaxed'>
        ヒント: 共通テーマと自由CSS は全コンポーネントに反映されます。コンポーネント個別の見た目調整はそれぞれのペインから行ってください。
      </div>
    </div>
  )
}

// ============================================================
// 共通ペイン: テーマ
// ============================================================

function ThemePane({ settings, updateTheme }) {
  return (
    <div className={`${CARD} p-[18px]`}>
      <h2 className='text-primary mb-3.5 text-base font-bold'>🎨 共通テーマ</h2>
      <p className='text-secondary mb-3 text-[11px]'>
        ウィジェット全体に適用されるテーマです。各コンポーネントは個別ペインで上書き可能。
      </p>
      <div className='grid grid-cols-2 gap-3.5'>
        <Field label="アクセント色"><ColorInput value={settings.rich_ui_theme.accent_color} onChange={v => updateTheme('accent_color', v)} /></Field>
        <Field label="背景色"><ColorInput value={settings.rich_ui_theme.bg_color} onChange={v => updateTheme('bg_color', v)} /></Field>
        <Field label="テキスト色"><ColorInput value={settings.rich_ui_theme.text_color} onChange={v => updateTheme('text_color', v)} /></Field>
        <Field label="border-radius"><input value={settings.rich_ui_theme.border_radius} onChange={e => updateTheme('border_radius', e.target.value)} placeholder="12px" className={INPUT} /></Field>
        <Field label="フォントサイズ"><input value={settings.rich_ui_theme.font_size} onChange={e => updateTheme('font_size', e.target.value)} placeholder="13px" className={INPUT} /></Field>
      </div>
    </div>
  )
}

// ============================================================
// 共通ペイン: 自由CSS
// ============================================================

function CssPane({ settings, setSettings }) {
  return (
    <div className={`${CARD} p-[18px]`}>
      <h2 className='text-primary mb-2 text-base font-bold'>📝 自由CSS（全体スコープ）</h2>
      <p className='text-secondary mb-2 text-[11px]'>
        ウィジェットルート要素 (#pa-widget-XXX) にスコープして注入されます。例: <code>--pa-accent: #ff6b6b;</code>
      </p>
      <textarea
        value={settings.rich_ui_custom_css}
        onChange={e => setSettings(prev => ({ ...prev, rich_ui_custom_css: e.target.value }))}
        rows={16}
        className={`${TEXTAREA} font-mono`}
        placeholder={`/* 例 */\n--pa-accent: #ff6b6b;\nfont-family: 'Noto Sans JP', sans-serif;`}
      />
    </div>
  )
}

// ============================================================
// 共通ペイン: プレビュー
// ============================================================

function PreviewPane({ settings }) {
  const t = settings.rich_ui_theme
  return (
    <div className={`${CARD} p-[18px]`}>
      <h2 className='text-primary mb-3.5 text-base font-bold'>👁️ プレビュー</h2>
      <p className='text-secondary mb-2.5 text-[11px]'>
        会話パネルの既定幅（380px）で表示しています。実際の幅・形態（サイドパネル／ボトムシート／
        インライン枠）は「配信設定 &gt; デザイン」で変わります。インライン枠は幅320px未満だと
        パネル引き継ぎに切り替わるため、コンポーネントは概ねこの幅で崩れないことが前提です。
      </p>
      <div style={{ padding: 16, background: '#f3f4f6', borderRadius: 8 }}>
        <div style={{
          width: 380, margin: '0 auto',
          background: t.bg_color, color: t.text_color,
          borderRadius: t.border_radius, fontSize: t.font_size,
          overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        }}>
          <div style={{ padding: '10px 14px', background: t.accent_color, color: '#fff', fontWeight: 700 }}>🛒 AIに相談</div>
          <div style={{ padding: 12 }}>
            <div style={{ padding: 8, background: '#f3f4f6', borderRadius: 10 }}>通勤におすすめのコートを見せて。</div>
            <div style={{ marginTop: 8, padding: 10, border: '1px solid #e5e7eb', borderRadius: 8, background: '#fafafa' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <SamplePreviewCard name="ウールコート" price="¥28,800" />
                <SamplePreviewCard name="3WAYダウン" price="¥24,500" />
              </div>
            </div>
            <div style={{ marginTop: 6, display: 'flex', gap: 4 }}>
              {['もっと安いものは？', '1番人気を詳しく'].map(c => (
                <button key={c} style={{ padding: '5px 10px', fontSize: 11, background: 'rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.1)', color: t.accent_color, borderRadius: 12, cursor: 'pointer' }}>{c}</button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function SamplePreviewCard({ name, price }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
      <div style={{ aspectRatio: '1/1', background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: 24 }}>📷</div>
      <div style={{ padding: 6 }}>
        <div style={{ fontSize: 12, fontWeight: 500, color: '#333' }}>{name}</div>
        <div style={{ fontSize: 13, fontWeight: 600, marginTop: 4, color: '#111' }}>{price}</div>
      </div>
    </div>
  )
}

// ============================================================
// コンポーネント別ペイン（共通枠 + 個別中身）
// ============================================================

function ComponentPane({ navItem, settings, toggleComponent, getOptions, updateOptions, siteId, theme, site }) {
  const enabled = settings.rich_ui_components_enabled.includes(navItem.key)
  const opts = getOptions(navItem.key)
  const set = (k, v) => updateOptions(navItem.key, { [k]: v })

  const detail = renderComponentDetail({ navItem, opts, set, site })

  return (
    <div className={`${CARD} p-[18px]`}>
      <div className='mb-3.5 flex items-center gap-2.5'>
        <h2 className='text-primary m-0 flex-1 text-base font-bold'>{navItem.label}</h2>
        {navItem.requiresContract &&
          (site?.[navItem.requiresContract] ? (
            <span className='bg-surface-success text-success rounded-full px-2 py-0.5 text-[10px] font-semibold'>
              {CONTRACT_LABELS[navItem.requiresContract]} 契約済み
            </span>
          ) : (
            <span className='bg-surface-warning text-warning rounded-full px-2 py-0.5 text-[10px] font-semibold'>
              {CONTRACT_LABELS[navItem.requiresContract]} 未契約
            </span>
          ))}
        <label className='text-secondary text-[13px]'>
          <input type="checkbox" checked={enabled} onChange={() => toggleComponent(navItem.key)} className='mr-1' />
          このコンポーネントを使う
        </label>
      </div>
      {!enabled && (
        <div className='bg-surface-secondary text-secondary mb-3.5 rounded-lg p-2.5 text-xs'>
          現在このコンポーネントは無効です。チェックを入れると AI が会話文脈に応じて自動選択するようになります。
        </div>
      )}
      {/* 設定 + ライブプレビューを横に並べる */}
      <div className='grid grid-cols-[1fr_340px] items-start gap-3.5'>
        <div>{detail}</div>
        <div className='sticky top-3.5'>
          <div className='text-secondary mb-2 text-[12px] font-semibold'>👁️ ライブプレビュー</div>
          <div style={{ padding: 12, background: '#f3f4f6', borderRadius: 8 }}>
            <ComponentLivePreview componentKey={navItem.key} opts={opts} theme={theme} />
          </div>
        </div>
      </div>
      {/* プリセット管理 */}
      {navItem.preset && (
        <PresetManager kind={navItem.preset} siteId={siteId} />
      )}
    </div>
  )
}

// ============================================================
// 各コンポーネントのライブプレビュー
// ============================================================

const SAMPLE_PRODUCTS = [
  { name: 'プレミアムウールコート', price: 28800, image: '🧥' },
  { name: '3WAYダウンコート', price: 24500, image: '🧥' },
  { name: 'カシミヤニット', price: 18900, image: '👚' },
  { name: 'シルクブラウス', price: 12800, image: '👔' },
]

function ComponentLivePreview({ componentKey, opts, theme }) {
  const t = theme || {}
  const accent = t.accent_color || '#6366f1'
  const radius = t.border_radius || '12px'
  const tc = t.text_color || '#111111'
  const bg = t.bg_color || '#ffffff'
  switch (componentKey) {
    case 'product_gallery': return <PreviewProductGallery opts={opts} accent={accent} radius={radius} bg={bg} tc={tc} />
    case 'hero_card': return <PreviewHeroCard opts={opts} accent={accent} radius={radius} bg={bg} tc={tc} />
    case 'comparison_table': return <PreviewComparisonTable opts={opts} accent={accent} radius={radius} bg={bg} tc={tc} />
    case 'comparison_radar': return <PreviewComparisonRadar opts={opts} accent={accent} radius={radius} bg={bg} tc={tc} />
    case 'personalized_cards': return <PreviewPersonalizedCards opts={opts} accent={accent} radius={radius} bg={bg} tc={tc} />
    case 'quick_choice': return <PreviewQuickChoice opts={opts} accent={accent} radius={radius} bg={bg} tc={tc} />
    case 'filter_slider': return <PreviewFilterSlider opts={opts} accent={accent} radius={radius} bg={bg} tc={tc} />
    case 'review_highlights': return <PreviewReviewHighlights opts={opts} accent={accent} radius={radius} bg={bg} tc={tc} />
    case 'bundle_offer': return <PreviewBundleOffer opts={opts} accent={accent} radius={radius} bg={bg} tc={tc} />
    default: return null
  }
}

function PvCard({ children, custom }) {
  // 全プレビュー共通の外枠（ウィジェット内のカードらしい見た目）
  return (
    <div style={{ background: '#fff', borderRadius: 8, overflow: 'hidden', border: '1px solid #e5e7eb', ...custom }}>
      {children}
    </div>
  )
}

function PreviewProductGallery({ opts, accent, radius }) {
  const max = Math.min(opts.max_items || 6, SAMPLE_PRODUCTS.length)
  const products = SAMPLE_PRODUCTS.slice(0, max)
  const cols = opts.grid_columns || 2
  const aspect = opts.image_aspect_ratio || '1/1'
  const isScroll = opts.layout === 'horizontal_scroll'
  const gridStyle = isScroll
    ? { display: 'flex', gap: 6, overflowX: 'auto', padding: 6 }
    : { display: 'grid', gridTemplateColumns: `repeat(${cols},1fr)`, gap: 6, padding: 6 }
  return (
    <PvCard>
      <div style={gridStyle}>
        {products.map((p, i) => {
          const widthCss = isScroll ? { flex: `0 0 ${100 / cols - 4}%` } : {}
          const span = opts.emphasis_first && i === 0 && !isScroll ? { gridColumn: `span ${cols}` } : {}
          return (
            <div key={i} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden', ...widthCss, ...span }}>
              <div style={{ aspectRatio: aspect, background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>{p.image}</div>
              <div style={{ padding: 4 }}>
                <div style={{ fontSize: 10, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                <div style={{ fontSize: 11, fontWeight: 600 }}>¥{p.price.toLocaleString()}</div>
              </div>
            </div>
          )
        })}
      </div>
    </PvCard>
  )
}

function PreviewHeroCard({ opts, accent, radius }) {
  const animClass = `pa-pv-${opts.default_animation_in || 'fade_in'}`
  const bgStyle = opts.default_background_type === 'solid'
    ? { background: '#1a1a1a' }
    : { background: 'linear-gradient(135deg,#1a1a1a,#3a3a3a)' }
  return (
    <>
      <style>{`
        @keyframes pvFadeIn{from{opacity:0}to{opacity:1}}
        @keyframes pvSlideUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
        @keyframes pvZoom{from{opacity:0;transform:scale(.92)}to{opacity:1;transform:none}}
        .pa-pv-fade_in{animation:pvFadeIn .3s ease}
        .pa-pv-slide_up{animation:pvSlideUp .35s ease}
        .pa-pv-zoom{animation:pvZoom .35s ease}
      `}</style>
      <PvCard custom={{ borderRadius: radius }}>
        <div className={animClass} style={{ aspectRatio: '16/10', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', ...bgStyle }}>
          <div style={{ position: 'absolute', top: 6, left: 6, background: 'rgba(255,255,255,0.95)', color: '#000', padding: '2px 6px', borderRadius: 3, fontSize: 9, fontWeight: 600 }}>今シーズンNo.1</div>
          <div style={{ color: '#fff', fontSize: 32 }}>🧥</div>
        </div>
        <div style={{ padding: 8, color: '#111' }}>
          <div style={{ fontSize: 12, fontWeight: 600 }}>プレミアムウールコート</div>
          <div style={{ fontSize: 14, fontWeight: 700, marginTop: 2 }}>¥28,800</div>
          <div style={{ background: 'rgba(99,102,241,0.08)', padding: 5, borderRadius: 4, fontSize: 10, marginTop: 6 }}>💡 通勤に最適、軽量で暖かい</div>
          <button style={{ marginTop: 6, width: '100%', padding: 6, background: '#000', color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>カートに追加</button>
        </div>
      </PvCard>
      <div className='text-tertiary mt-1.5 text-[10px]'>
        ※ プリセットがマッチした時はプリセットの設定が優先されます
      </div>
    </>
  )
}

function PreviewComparisonTable({ opts, accent }) {
  const hi = opts.recommended_highlight_color || '#eef2ff'
  const hbg = opts.header_bg_color || '#f9fafb'
  const recIdx = 1
  const rows = [
    { label: '価格', vals: ['¥28,800', '¥24,500', '¥18,900'], bestIdx: 2 },
    { label: '保温性', vals: ['★★★★★', '★★★★', '★★★'], bestIdx: 0 },
    { label: '軽量', vals: ['600g', '1,200g', '800g'], bestIdx: 0 },
  ]
  return (
    <PvCard>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10, color: '#111' }}>
        <thead>
          <tr>
            <th style={{ background: hbg, padding: 4, textAlign: 'left' }}>項目</th>
            {['コートA', 'コートB', 'コートC'].map((n, i) => (
              <th key={i} style={{ background: i === recIdx ? hi : hbg, padding: 4, textAlign: 'left' }}>{i === recIdx ? '★ ' : ''}{n}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri}>
              <td style={{ padding: 4, fontWeight: 500 }}>{r.label}</td>
              {r.vals.map((v, i) => (
                <td key={i} style={{
                  padding: 4,
                  background: i === recIdx ? hi : 'transparent',
                  fontWeight: opts.highlight_best_value && i === r.bestIdx ? 700 : 400,
                }}>{v}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </PvCard>
  )
}

function PreviewComparisonRadar({ opts }) {
  const sizeMap = { small: 140, medium: 180, large: 220 }
  const palettes = {
    'default': ['#3b82f6', '#ef4444', '#10b981'],
    'warm': ['#ef4444', '#f97316', '#f59e0b'],
    'cool': ['#3b82f6', '#10b981', '#8b5cf6'],
    'mono': ['#374151', '#6b7280', '#9ca3af'],
  }
  const palette = palettes[opts.color_palette] || palettes.default
  const size = sizeMap[opts.size || 'medium']
  const c = size / 2, maxR = c - 18, ax = 5
  const axisColor = opts.axis_color || '#e5e7eb'
  const opacity = (opts.fill_opacity || 25) / 100
  const sample = [[80, 60, 90, 75, 65], [70, 90, 60, 85, 80], [60, 70, 80, 70, 70]]
  const axes = ['価格', '保温性', '軽量', 'デザイン', '耐久性']
  return (
    <PvCard custom={{ padding: 8 }}>
      <svg viewBox={`0 0 ${size} ${size}`} style={{ width: '100%', maxWidth: size, display: 'block', margin: '0 auto' }}>
        {[0.25, 0.5, 0.75, 1].map(r => (
          <polygon key={r} points={Array.from({ length: ax }).map((_, i) => {
            const t = Math.PI * 2 * i / ax - Math.PI / 2
            return `${c + Math.cos(t) * maxR * r},${c + Math.sin(t) * maxR * r}`
          }).join(' ')} fill="none" stroke={axisColor} />
        ))}
        {axes.map((a, i) => {
          const t = Math.PI * 2 * i / ax - Math.PI / 2
          return <text key={i} x={c + Math.cos(t) * (maxR + 8)} y={c + Math.sin(t) * (maxR + 8)} fontSize="9" textAnchor="middle" dominantBaseline="middle" fill="#374151">{a}</text>
        })}
        {sample.map((s, pi) => (
          <polygon key={pi} points={s.map((v, i) => {
            const t = Math.PI * 2 * i / ax - Math.PI / 2
            return `${c + Math.cos(t) * maxR * v / 100},${c + Math.sin(t) * maxR * v / 100}`
          }).join(' ')} fill={palette[pi]} fillOpacity={opacity} stroke={palette[pi]} strokeWidth="1.5" />
        ))}
      </svg>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6, fontSize: 10, color: '#333' }}>
        {['コートA', 'コートB', 'コートC'].map((n, i) => (
          <span key={i}><span style={{ display: 'inline-block', width: 7, height: 7, background: palette[i], borderRadius: '50%', marginRight: 3 }}></span>{n}</span>
        ))}
      </div>
    </PvCard>
  )
}

function PreviewPersonalizedCards({ opts, accent }) {
  const reasonBg = opts.reason_box_color || 'rgba(99,102,241,0.06)'
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6, color: '#fff' }}>こんにちは、田中さん</div>
      {opts.show_segment_label && (
        <div style={{ display: 'inline-block', padding: '2px 6px', background: 'rgba(99,102,241,0.2)', borderRadius: 8, fontSize: 9, marginBottom: 6, color: '#fff' }}>ダウンコート愛用者</div>
      )}
      <PvCard>
        <div style={{ display: 'flex', gap: 8, padding: 8 }}>
          <div style={{ width: 48, height: 48, background: '#eee', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>🧥</div>
          <div style={{ flex: 1, color: '#111' }}>
            <div style={{ fontSize: 11, fontWeight: 600 }}>プレミアムダウン</div>
            <div style={{ fontSize: 12, fontWeight: 700, marginTop: 2 }}>¥28,800</div>
            {opts.show_relevance_score && <div style={{ fontSize: 10, color: '#666', marginTop: 2 }}>関連性 92%</div>}
          </div>
        </div>
        <div style={{ padding: '6px 8px', background: reasonBg, borderLeft: `3px solid ${accent}`, fontSize: 10, color: '#111' }}>💡 昨年購入したコートの新色版</div>
      </PvCard>
    </div>
  )
}

function PreviewQuickChoice({ opts, accent }) {
  const iconBg = opts.default_icon_bg_color || '#fafafa'
  const padMap = { compact: '8px 6px', standard: '14px 8px', large: '20px 10px' }
  const pad = padMap[opts.card_size || 'standard']
  return (
    <PvCard custom={{ padding: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#111' }}>主な着用シーンは？</div>
      <div style={{ fontSize: 9, color: '#666', marginBottom: 6 }}>シーンに応じて素材が変わるため</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        {[
          { icon: '💼', label: '通勤', sub: 'きれいめ' },
          { icon: '🏔️', label: '休日', sub: '機能性' },
        ].map((c, i) => (
          <div key={i} style={{ padding: pad, background: iconBg, border: '1px solid #e5e7eb', borderRadius: 6, textAlign: 'center' }}>
            <div style={{ fontSize: 20, marginBottom: 4 }}>{c.icon}</div>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#111' }}>{c.label}</div>
            <div style={{ fontSize: 9, color: '#666' }}>{c.sub}</div>
          </div>
        ))}
      </div>
    </PvCard>
  )
}

function PreviewFilterSlider({ opts, accent }) {
  const sliderColor = opts.slider_accent_color || accent
  const previewN = Math.min(opts.preview_count || 3, 3)
  return (
    <PvCard custom={{ padding: 8 }}>
      <div style={{ fontSize: 10, color: '#666', marginBottom: 4 }}>価格帯</div>
      <input type="range" min={0} max={100} defaultValue={60} style={{ width: '100%', accentColor: sliderColor }} />
      <div style={{ fontSize: 10, textAlign: 'right', color: '#111' }}>¥30,000</div>
      <div style={{ fontSize: 11, fontWeight: 600, margin: '8px 0 4px', color: '#111' }}>24件の商品</div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${previewN},1fr)`, gap: 4 }}>
        {Array.from({ length: previewN }).map((_, i) => (
          <div key={i} style={{ background: '#eee', aspectRatio: '1/1', borderRadius: 4, fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>📷</div>
        ))}
      </div>
      {opts.show_view_all && <button style={{ width: '100%', marginTop: 6, padding: 4, fontSize: 10, background: 'transparent', border: `1px solid ${accent}`, color: accent, borderRadius: 3 }}>全て見る →</button>}
    </PvCard>
  )
}

function PreviewReviewHighlights({ opts }) {
  const pos = opts.positive_color || '#198754'
  const con = opts.concern_color || '#b45309'
  const maxP = Math.min(opts.max_positive || 3, 3)
  const maxC = Math.min(opts.max_concern || 2, 2)
  const positives = ['期待以上に暖かい', '軽量で動きやすい', 'デザインが上品'].slice(0, maxP)
  const concerns = ['少しサイズが大きめ', '価格が高め'].slice(0, maxC)
  return (
    <PvCard>
      <div style={{ padding: 6, borderBottom: '1px solid #e5e7eb', display: 'flex', gap: 6, alignItems: 'center' }}>
        <div style={{ width: 32, height: 32, background: '#eee', borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🧥</div>
        <div style={{ color: '#111' }}>
          <div style={{ fontSize: 11, fontWeight: 600 }}>プレミアムコート</div>
          <div style={{ fontSize: 10, color: '#666' }}>★ 4.8 (234件)</div>
        </div>
      </div>
      <div style={{ padding: 6 }}>
        <div style={{ fontSize: 10, fontWeight: 600, color: pos, marginBottom: 3 }}>👍 良い点</div>
        {positives.map((p, i) => <div key={i} style={{ fontSize: 10, color: '#333', margin: '1px 0' }}>・{p}</div>)}
      </div>
      {concerns.length > 0 && (
        <div style={{ padding: 6, borderTop: '1px solid #f0f0f0' }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: con, marginBottom: 3 }}>⚠️ 気になる点</div>
          {concerns.map((c, i) => <div key={i} style={{ fontSize: 10, color: '#333', margin: '1px 0' }}>・{c}</div>)}
        </div>
      )}
    </PvCard>
  )
}

function PreviewBundleOffer({ opts, accent }) {
  const headerColor = opts.default_header_color || '#0d6efd'
  return (
    <PvCard>
      <div style={{ padding: 8, background: headerColor, color: '#fff' }}>
        <div style={{ fontSize: 11, fontWeight: 600 }}>📦 キャンプ初心者セット</div>
        <div style={{ fontSize: 10, marginTop: 2, opacity: 0.9 }}>単品より¥4,200お得</div>
        <div style={{ fontSize: 12, fontWeight: 700, marginTop: 2 }}>¥34,200 <span style={{ fontSize: 10, opacity: 0.7, textDecoration: 'line-through' }}>¥38,400</span></div>
      </div>
      {[
        { icon: '⛺', name: 'テント', price: 15800 },
        { icon: '🛏️', name: 'シュラフ', price: 8900 },
        { icon: '🔦', name: 'ランタン', price: 4800 },
      ].map((p, i) => (
        <div key={i} style={{ padding: '4px 8px', borderTop: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, color: '#111' }}>
          <span style={{ fontSize: 16 }}>{p.icon}</span>
          <span style={{ flex: 1 }}>{p.name}</span>
          <span>¥{p.price.toLocaleString()}</span>
        </div>
      ))}
      <button style={{ width: '100%', padding: 6, background: '#000', color: '#fff', border: 'none', fontWeight: 600, fontSize: 11, cursor: 'pointer' }}>セットでカートに追加</button>
    </PvCard>
  )
}

function renderComponentDetail({ navItem, opts, set, site }) {
  switch (navItem.key) {
    case 'product_gallery': return <ProductGalleryOptions opts={opts} set={set} />
    case 'hero_card': return <HeroCardOptions opts={opts} set={set} />
    case 'comparison_table': return <ComparisonTableOptions opts={opts} set={set} />
    case 'comparison_radar': return <ComparisonRadarOptions opts={opts} set={set} />
    case 'personalized_cards': return <PersonalizedCardsOptions opts={opts} set={set} site={site} />
    case 'quick_choice': return <QuickChoiceOptions opts={opts} set={set} />
    case 'filter_slider': return <FilterSliderOptions opts={opts} set={set} />
    case 'review_highlights': return <ReviewHighlightsOptions opts={opts} set={set} site={site} />
    case 'bundle_offer': return <BundleOfferOptions opts={opts} set={set} />
    default: return null
  }
}

// --------- 各コンポーネントの個別オプション ---------

function ProductGalleryOptions({ opts, set }) {
  return (
    <Section title="個別オプション">
      <Row>
        <Field label="表示数（4-8）"><input type="number" min={2} max={8} value={opts.max_items} onChange={e => set('max_items', Number(e.target.value))} className={INPUT} /></Field>
        <Field label="レイアウト">
          <select value={opts.layout} onChange={e => set('layout', e.target.value)} className={INPUT}>
            <option value="grid">グリッド</option>
            <option value="horizontal_scroll">横スクロール</option>
          </select>
        </Field>
        <Field label="グリッド列数">
          <select value={opts.grid_columns} onChange={e => set('grid_columns', Number(e.target.value))} className={INPUT} disabled={opts.layout !== 'grid'}>
            <option value={2}>2列</option><option value={3}>3列</option>
          </select>
        </Field>
      </Row>
      <Row>
        <Field label="画像比率">
          <select value={opts.image_aspect_ratio} onChange={e => set('image_aspect_ratio', e.target.value)} className={INPUT}>
            <option value="1/1">正方形 (1:1)</option>
            <option value="4/3">4:3</option>
            <option value="16/9">16:9</option>
            <option value="3/4">縦長 (3:4)</option>
          </select>
        </Field>
        <Field label="1番目の商品を強調表示">
          <label className='text-secondary text-[12px]'><input type="checkbox" checked={!!opts.emphasis_first} onChange={e => set('emphasis_first', e.target.checked)} /> 有効</label>
        </Field>
      </Row>
      <CssField value={opts.custom_css} onChange={v => set('custom_css', v)} />
    </Section>
  )
}

function HeroCardOptions({ opts, set }) {
  return (
    <Section title="LLM動的生成時のデフォルト動作">
      <p className='text-tertiary mb-2 text-[11px]'>プリセットがマッチしない時の Hero カードのフォールバック設定です。</p>
      <Row>
        <Field label="入場アニメーション">
          <select value={opts.default_animation_in} onChange={e => set('default_animation_in', e.target.value)} className={INPUT}>
            <option value="none">なし</option>
            <option value="fade_in">フェードイン</option>
            <option value="slide_up">スライドアップ</option>
            <option value="zoom">ズーム</option>
          </select>
        </Field>
        <Field label="背景タイプ">
          <select value={opts.default_background_type} onChange={e => set('default_background_type', e.target.value)} className={INPUT}>
            <option value="gradient">グラデ</option>
            <option value="solid">単色</option>
          </select>
        </Field>
        <Field label="CTAホバー効果">
          <select value={opts.default_cta_hover_effect} onChange={e => set('default_cta_hover_effect', e.target.value)} className={INPUT}>
            <option value="none">なし</option>
            <option value="lift">浮き上がり</option>
            <option value="glow">発光</option>
            <option value="pulse">パルス</option>
          </select>
        </Field>
      </Row>
      <CssField value={opts.custom_css} onChange={v => set('custom_css', v)} />
    </Section>
  )
}

function ComparisonTableOptions({ opts, set }) {
  return (
    <Section title="個別オプション">
      <Row>
        <Field label="推奨列のハイライト色"><ColorInput value={opts.recommended_highlight_color} onChange={v => set('recommended_highlight_color', v)} /></Field>
        <Field label="ヘッダー背景色"><ColorInput value={opts.header_bg_color} onChange={v => set('header_bg_color', v)} /></Field>
      </Row>
      <Field label="最良値ハイライト">
        <label className='text-secondary text-[12px]'><input type="checkbox" checked={!!opts.highlight_best_value} onChange={e => set('highlight_best_value', e.target.checked)} /> 最良値セルを強調表示</label>
      </Field>
      <CssField value={opts.custom_css} onChange={v => set('custom_css', v)} />
    </Section>
  )
}

function ComparisonRadarOptions({ opts, set }) {
  return (
    <Section title="個別オプション">
      <Row>
        <Field label="カラーパレット">
          <select value={opts.color_palette} onChange={e => set('color_palette', e.target.value)} className={INPUT}>
            <option value="default">既定（ブルー系）</option>
            <option value="warm">ウォーム（赤橙黄）</option>
            <option value="cool">クール（青緑紫）</option>
            <option value="mono">モノクロ</option>
          </select>
        </Field>
        <Field label="サイズ">
          <select value={opts.size} onChange={e => set('size', e.target.value)} className={INPUT}>
            <option value="small">小</option><option value="medium">中</option><option value="large">大</option>
          </select>
        </Field>
      </Row>
      <Row>
        <Field label="軸線色"><ColorInput value={opts.axis_color} onChange={v => set('axis_color', v)} /></Field>
        <Field label="塗り透過率（%）"><input type="number" min={10} max={80} value={opts.fill_opacity} onChange={e => set('fill_opacity', Number(e.target.value))} className={INPUT} /></Field>
      </Row>
      <CssField value={opts.custom_css} onChange={v => set('custom_css', v)} />
    </Section>
  )
}

// パーソナライズで選べるレコメンドロジック。
// **バックエンドの services/pa_reco_bridge.py の LOGIC_INFO と値を揃えること。**
// ここに無い値を保存しても、配信側が知らないため使われない
const RECOMMEND_LOGIC_CHOICES = [
  { key: 'BB', label: 'この商品を買った人はこちらも購入', hint: '併売を薦めたいとき', needsItem: true },
  { key: 'OB', label: 'この商品を見た人はこちらを購入', hint: '迷っている相手に、最終的に選ばれた商品を見せたいとき', needsItem: true },
  { key: 'similar', label: '似ている商品', hint: '価格帯や特徴が合わないと言われたとき', needsItem: true },
  { key: 'personal', label: 'あなたへのおすすめ', hint: '閲覧・購入履歴をもとに薦めたいとき', needsItem: false },
  { key: 'adaptive', label: 'いまのあなたに', hint: '初回訪問・検討中・購入済みなど状況に合わせたいとき', needsItem: false },
  { key: 'ranking_blend', label: '人気のなかから', hint: '履歴が乏しい相手に人気商品を軸に薦めたいとき', needsItem: false },
  { key: 'ranking', label: '人気商品', hint: '手がかりが少なく、まず売れているものを見せたいとき', needsItem: false },
  { key: 'recently_viewed', label: '最近見た商品の続き', hint: '検討を再開させたいとき', needsItem: false },
]

function PersonalizedCardsOptions({ opts, set, site }) {
  // Reco-Media を契約していないサイトでは、ロジックを選べない。
  // 「有効にはできるがデータが無いので出ない」状態は、運用者から見て不具合と区別が付かない
  const recoEnabled = !!site?.enable_reco_media
  const logics = opts.logics || []
  const badges = opts.reason_badges || {}

  const toggleLogic = (key) => {
    set('logics', logics.includes(key) ? logics.filter(l => l !== key) : [...logics, key])
  }
  const setBadge = (key, field, value) => {
    set('reason_badges', { ...badges, [key]: { ...(badges[key] || {}), [field]: value } })
  }

  return (
    <Section title="個別オプション">
      <Field label="使うレコメンドロジック">
        {!recoEnabled ? (
          <div className='bg-surface-secondary text-secondary rounded-lg p-3 text-[12px]'>
            Reco-Media を契約しているサイトでのみ設定できます。
          </div>
        ) : (
          <>
            <p className='text-tertiary mb-2 text-[11px]'>
              選んだロジックの中から、AIが会話の流れに合うものを選んで商品を提示します。
              1つも選ばないとパーソナライズは表示されません。
            </p>
            <div className='flex flex-col gap-1.5'>
              {RECOMMEND_LOGIC_CHOICES.map(({ key, label, hint, needsItem }) => {
                const on = logics.includes(key)
                return (
                  <div key={key} className='border-secondary rounded-lg border p-2'>
                    <label className='text-secondary flex items-start gap-2 text-[12px]'>
                      <input type="checkbox" checked={on} onChange={() => toggleLogic(key)} className='mt-0.5' />
                      <span>
                        <span className='text-primary font-semibold'>{label}</span>
                        {needsItem && <span className='text-tertiary ml-1'>（商品の話題があるときに使われます）</span>}
                        <span className='text-tertiary block'>{hint}</span>
                      </span>
                    </label>
                    {on && (
                      /* 入力欄だけを並べると何の設定か分からないため、必ずラベルを付ける */
                      <div className='mt-2 pl-6'>
                        <div className='text-tertiary mb-1.5 text-[11px]'>
                          このロジックで商品を出したときに、カードの上に出す見出しです。
                        </div>
                        <div className='flex flex-wrap items-end gap-3'>
                          {/* 文言は長くなるので広く取る。色は固定幅で右に置く */}
                          <div className='min-w-[280px] flex-1'>
                            <div className='text-secondary mb-1 text-[11px]'>見出しの文言</div>
                            <input
                              value={badges[key]?.label ?? ''}
                              onChange={e => setBadge(key, 'label', e.target.value)}
                              placeholder={label}
                              className={INPUT}
                            />
                            <div className='text-tertiary mt-1 text-[11px]'>
                              未入力なら「{label}」と表示します
                            </div>
                          </div>
                          <div className='shrink-0'>
                            <div className='text-secondary mb-1 text-[11px]'>見出しの色</div>
                            <ColorInput value={badges[key]?.color} onChange={v => setBadge(key, 'color', v)} />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
            <p className='text-tertiary mt-2 text-[11px]'>
              ※ チェックを入れたロジックには、商品カードの上に出す見出しの文言と色を設定できます。
            </p>
          </>
        )}
      </Field>
      <Field label="推薦理由ボックスの背景色"><ColorInput value={opts.reason_box_color} onChange={v => set('reason_box_color', v)} /></Field>
      <Row>
        <Field label="関連性スコア表示">
          <label className='text-secondary text-[12px]'><input type="checkbox" checked={!!opts.show_relevance_score} onChange={e => set('show_relevance_score', e.target.checked)} /> 表示する</label>
        </Field>
        <Field label="セグメントラベル表示">
          <label className='text-secondary text-[12px]'><input type="checkbox" checked={!!opts.show_segment_label} onChange={e => set('show_segment_label', e.target.checked)} /> 表示する</label>
        </Field>
      </Row>
      <CssField value={opts.custom_css} onChange={v => set('custom_css', v)} />
    </Section>
  )
}

function QuickChoiceOptions({ opts, set }) {
  return (
    <Section title="LLM動的生成時のデフォルト">
      <p className='text-tertiary mb-2 text-[11px]'>プリセットがマッチしない時の二択カードのデフォルト見た目。</p>
      <Row>
        <Field label="アイコン背景色"><ColorInput value={opts.default_icon_bg_color} onChange={v => set('default_icon_bg_color', v)} /></Field>
        <Field label="カードサイズ">
          <select value={opts.card_size} onChange={e => set('card_size', e.target.value)} className={INPUT}>
            <option value="compact">コンパクト</option>
            <option value="standard">標準</option>
            <option value="large">大きめ</option>
          </select>
        </Field>
      </Row>
      <CssField value={opts.custom_css} onChange={v => set('custom_css', v)} />
    </Section>
  )
}

function FilterSliderOptions({ opts, set }) {
  return (
    <Section title="個別オプション">
      <Row>
        <Field label="スライダーアクセント色"><ColorInput value={opts.slider_accent_color} onChange={v => set('slider_accent_color', v)} /></Field>
        <Field label="プレビュー商品数"><input type="number" min={2} max={6} value={opts.preview_count} onChange={e => set('preview_count', Number(e.target.value))} className={INPUT} /></Field>
      </Row>
      <Field label="「全て見る」ボタンの表示">
        <label className='text-secondary text-[12px]'><input type="checkbox" checked={!!opts.show_view_all} onChange={e => set('show_view_all', e.target.checked)} /> 表示する</label>
      </Field>
      <CssField value={opts.custom_css} onChange={v => set('custom_css', v)} />
    </Section>
  )
}

function ReviewHighlightsOptions({ opts, set, site }) {
  // UGC を契約していないサイトでは、レビューを根拠にできない
  const reviewEnabled = !!site?.enable_review
  return (
    <Section title="個別オプション">
      {!reviewEnabled ? (
        <div className='bg-surface-secondary text-secondary mb-3 rounded-lg p-3 text-[12px]'>
          UGC（レビュー）を契約しているサイトでのみ使えます。
        </div>
      ) : (
        <Field label="AIに渡すレビュー件数">
          <input
            type="number" min={1} max={30}
            value={opts.max_reviews ?? 10}
            onChange={e => set('max_reviews', Number(e.target.value))}
            className={INPUT}
          />
          <p className='text-tertiary mt-1 text-[11px]'>
            話題の商品の掲載中レビューを新しい順にこの件数だけAIへ渡し、良い点・気になる点をその場でまとめます。
            多くするほど応答が遅く、費用も増えます（最大30件）。
          </p>
        </Field>
      )}
      <Row>
        <Field label="良い点ブロックの色"><ColorInput value={opts.positive_color} onChange={v => set('positive_color', v)} /></Field>
        <Field label="気になる点ブロックの色"><ColorInput value={opts.concern_color} onChange={v => set('concern_color', v)} /></Field>
      </Row>
      <Row>
        <Field label="良い点 表示件数（最大）"><input type="number" min={1} max={10} value={opts.max_positive} onChange={e => set('max_positive', Number(e.target.value))} className={INPUT} /></Field>
        <Field label="気になる点 表示件数（最大）"><input type="number" min={1} max={5} value={opts.max_concern} onChange={e => set('max_concern', Number(e.target.value))} className={INPUT} /></Field>
      </Row>
      <Field label="代表レビュー表示">
        <label className='text-secondary text-[12px]'><input type="checkbox" checked={!!opts.show_representative_review} onChange={e => set('show_representative_review', e.target.checked)} /> 表示する</label>
      </Field>
      <CssField value={opts.custom_css} onChange={v => set('custom_css', v)} />
    </Section>
  )
}

function BundleOfferOptions({ opts, set }) {
  return (
    <Section title="LLM動的生成時のデフォルト">
      <p className='text-tertiary mb-2 text-[11px]'>プリセットがマッチしない時のバンドル提案のデフォルト見た目。</p>
      <Row>
        <Field label="ヘッダー色"><ColorInput value={opts.default_header_color} onChange={v => set('default_header_color', v)} /></Field>
        <Field label="アクセント色"><ColorInput value={opts.default_accent_color} onChange={v => set('default_accent_color', v)} /></Field>
      </Row>
      <CssField value={opts.custom_css} onChange={v => set('custom_css', v)} />
    </Section>
  )
}

// ============================================================
// プリセット管理（Hero / Bundle / QuickChoice 共通フレーム）
// ============================================================

const PRESET_CONFIG = {
  hero: {
    title: '📌 Heroカードプリセット',
    listEndpoint: 'hero-presets',
    description: '手動で押し出したい商品の Hero カードを登録します。強制発火モード時はキーワードヒットで即時表示されます。',
  },
  bundle: {
    title: '📦 バンドルプリセット',
    listEndpoint: 'bundle-presets',
    description: '「初心者セット」「○○一式」などのクロスセル提案を登録します。',
  },
  quick_choice: {
    title: '🔀 二択カードプリセット',
    listEndpoint: 'quick-choice-presets',
    description: '意図絞り込みのための分岐質問テンプレートを登録します。',
  },
}

function PresetManager({ kind, siteId }) {
  const { authHeaders } = useAuth()
  const cfg = PRESET_CONFIG[kind]
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null)

  const fetchAll = async () => {
    setLoading(true)
    try {
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/${cfg.listEndpoint}`, { headers: authHeaders() })
      if (r.ok) {
        const d = await r.json()
        setItems(d.items || [])
      }
    } finally { setLoading(false) }
  }
  useEffect(() => { fetchAll() }, [siteId, kind])

  const remove = async (id) => {
    if (!confirm('このプリセットを削除しますか？')) return
    try {
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/${cfg.listEndpoint}/${id}`, { method: 'DELETE', headers: authHeaders() })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      fetchAll()
    } catch (e) { alert(e.message) }
  }

  if (editing !== null) {
    if (kind === 'hero') return <HeroPresetEditor siteId={siteId} preset={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchAll() }} />
    if (kind === 'bundle') return <BundlePresetEditor siteId={siteId} preset={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchAll() }} />
    if (kind === 'quick_choice') return <QuickChoicePresetEditor siteId={siteId} preset={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchAll() }} />
  }

  return (
    <div className='mt-6 border-t border-dashed border-secondary pt-4'>
      <div className='mb-1.5 flex items-center gap-2.5'>
        <h3 className='text-primary m-0 flex-1 text-sm font-bold'>{cfg.title}</h3>
        <Button color={ButtonColor.PRIMARY} onClick={() => setEditing('new')}>＋ 新規プリセット</Button>
      </div>
      <p className='text-secondary mb-2.5 text-[11px]'>{cfg.description}</p>
      {loading ? <div className='text-secondary text-xs'>読み込み中...</div>
        : items.length === 0 ? <div className='bg-surface-secondary text-secondary rounded-lg p-3.5 text-center text-xs'>まだプリセットがありません</div>
        : (
          <div className='grid gap-1.5'>
            {items.map(p => (
              <PresetRow key={p.id} kind={kind} item={p} onEdit={() => setEditing(p)} onRemove={() => remove(p.id)} />
            ))}
          </div>
        )}
    </div>
  )
}

function PresetRow({ kind, item: p, onEdit, onRemove }) {
  const titleText = kind === 'bundle' ? p.bundle_name : p.name
  const subText = kind === 'hero' ? `商品ID ${p.product_id}${p.hero_label ? ' / ' + p.hero_label : ''}`
                : kind === 'bundle' ? `${(p.items || []).length}点 / ${p.discount_type === 'percent' ? p.discount_value + '%OFF' : '¥' + (p.discount_value || 0) + ' OFF'}`
                : `Q: ${p.question}`
  return (
    <div className='bg-surface-secondary flex items-center gap-2 rounded-lg p-2.5'>
      <div className='min-w-0 flex-1'>
        <div className='flex items-center gap-1.5'>
          <span className='text-primary text-[13px] font-semibold'>{titleText}</span>
          <span className={`rounded-lg px-1.5 py-px text-[9px] font-semibold ${p.trigger_mode === 'force' ? 'bg-surface-critical text-critical' : 'bg-surface text-secondary'}`}>
            {p.trigger_mode === 'force' ? '🔥 強制' : p.trigger_mode === 'both' ? '両' : 'LLM'}
          </span>
          {!p.enabled && <span className='bg-surface text-tertiary rounded-lg px-1.5 py-px text-[9px] font-semibold'>無効</span>}
          {p.trigger_keywords && p.trigger_keywords.length > 0 && <span className='text-tertiary text-[10px]'>kw: {p.trigger_keywords.slice(0, 3).join(', ')}{p.trigger_keywords.length > 3 ? '...' : ''}</span>}
        </div>
        <div className='text-secondary mt-0.5 truncate text-[11px]'>{subText}</div>
      </div>
      <Button color={ButtonColor.SECONDARY} onClick={onEdit}>編集</Button>
      <Button color={ButtonColor.CRITICAL} onClick={onRemove}>削除</Button>
    </div>
  )
}

// ============================================================
// プリセットエディタ（Hero / Bundle / QuickChoice）
// ============================================================

function HeroPresetEditor({ siteId, preset, onClose, onSaved }) {
  const { authHeaders } = useAuth()
  const isNew = !preset
  const [form, setForm] = useState(() => preset ? { ...preset } : {
    name: '', description: '', enabled: true, priority: 100,
    trigger_mode: 'llm_select', trigger_keywords: [], trigger_match_method: 'partial',
    target_categories: [], campaign_ids: [],
    product_id: '', hero_label: '今シーズンNo.1',
    recommend_reason_primary: '', recommend_reason_factors: [], recommend_reason_source: 'editorial',
    cta_primary_label: 'カートに追加', cta_primary_action: 'add_to_cart', cta_primary_prompt: '',
    cta_secondary_label: '', cta_secondary_action: '', cta_secondary_prompt: '',
    background_type: 'gradient', background_primary_color: '#1a1a1a', background_secondary_color: '#3a3a3a', background_image_url: '',
    accent_color: '#000000', text_color: '#ffffff', border_radius: '12px', custom_css: '',
    animation_in: 'fade_in', urgency_pulse: false, cta_hover_effect: 'lift',
    urgency_type: '', urgency_message: '',
  })
  const [keywordInput, setKeywordInput] = useState('')
  const [factorInput, setFactorInput] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }))

  const save = async () => {
    const errors = validateHeroForm(form)
    if (errors.length) { setError(errors.join(' / ')); return }
    setSaving(true); setError(null)
    try {
      const body = { ...form }
      if (body.urgency_type === '') body.urgency_type = null
      const url = isNew
        ? `/api/sites/${siteId}/purchase-assist/hero-presets`
        : `/api/sites/${siteId}/purchase-assist/hero-presets/${preset.id}`
      const r = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!r.ok) throw new Error(await presetErrorMessage(r))
      onSaved()
    } catch (e) { setError(e.message) } finally { setSaving(false) }
  }

  return (
    <div>
      <EditorHeader title={isNew ? '新規Heroカード' : `編集: ${preset.name}`} onClose={onClose} onSave={save} saving={saving} error={error} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 14 }}>
        <div>
          <Section title="基本情報">
            <Field label="プリセット名 *"><input value={form.name} onChange={e => update('name', e.target.value)} className={INPUT} /></Field>
            <Field label="説明（運用メモ）"><input value={form.description} onChange={e => update('description', e.target.value)} className={INPUT} /></Field>
            <Field label="商品ID *">
              <input value={form.product_id} onChange={e => update('product_id', e.target.value)} className={INPUT} placeholder="W2 商品ID（external_id）" />
              <ProductLookup siteId={siteId} productId={form.product_id} />
            </Field>
            <Row>
              <Field label="優先度（小=優先）"><input type="number" value={form.priority} onChange={e => update('priority', Number(e.target.value))} className={INPUT} /></Field>
              <Field label="有効/無効">
                <select value={form.enabled ? '1' : '0'} onChange={e => update('enabled', e.target.value === '1')} className={INPUT}>
                  <option value="1">有効</option><option value="0">無効</option>
                </select>
              </Field>
            </Row>
          </Section>

          <Section title="マッチング条件">
            <Field label="発火モード">
              <select value={form.trigger_mode} onChange={e => update('trigger_mode', e.target.value)} className={INPUT}>
                <option value="llm_select">LLM選択時のみ</option>
                <option value="force">🔥 強制発火（キーワードヒットで即表示）</option>
                <option value="both">両方</option>
              </select>
            </Field>
            <KeywordInput label="トリガーキーワード" keywords={form.trigger_keywords || []} input={keywordInput} setInput={setKeywordInput} onChange={v => update('trigger_keywords', v)} placeholder="通勤、ビジネス" />
            <Field label="マッチ方式">
              <select value={form.trigger_match_method} onChange={e => update('trigger_match_method', e.target.value)} className={INPUT}>
                <option value="partial">部分一致</option><option value="exact">完全一致</option><option value="regex">正規表現</option>
              </select>
            </Field>
          </Section>

          <Section title="コンテンツ">
            <Field label="Heroラベル"><input value={form.hero_label} onChange={e => update('hero_label', e.target.value)} className={INPUT} placeholder="今シーズンNo.1" /></Field>
            <Field label="メイン推薦理由 *"><textarea value={form.recommend_reason_primary} onChange={e => update('recommend_reason_primary', e.target.value)} rows={2} className={TEXTAREA} /></Field>
            <KeywordInput label="補足要因" keywords={form.recommend_reason_factors || []} input={factorInput} setInput={setFactorInput} onChange={v => update('recommend_reason_factors', v)} placeholder="例: レビュー★4.8" />
          </Section>

          <Section title="緊急性">
            <Field label="種別">
              <select value={form.urgency_type || ''} onChange={e => update('urgency_type', e.target.value)} className={INPUT}>
                <option value="">表示なし</option>
                <option value="time_limited">期間限定</option>
                <option value="stock_limited">在庫限定</option>
                <option value="price_drop">値下げ</option>
              </select>
            </Field>
            <Field label="メッセージ"><input value={form.urgency_message} onChange={e => update('urgency_message', e.target.value)} className={INPUT} placeholder="残り3点 / セール終了まで2日" /></Field>
            <Row>
              <Field label="カウントダウン目標日時"><input type="datetime-local" value={form.urgency_countdown_to ? form.urgency_countdown_to.slice(0, 16) : ''} onChange={e => update('urgency_countdown_to', e.target.value || null)} className={INPUT} /></Field>
              <Field label="点滅アニメ">
                <label className='text-secondary text-[12px]'><input type="checkbox" checked={form.urgency_pulse} onChange={e => update('urgency_pulse', e.target.checked)} /> ON</label>
              </Field>
            </Row>
          </Section>

          <Section title="CTAボタン">
            <Row>
              <Field label="プライマリ ラベル"><input value={form.cta_primary_label} onChange={e => update('cta_primary_label', e.target.value)} className={INPUT} /></Field>
              <Field label="プライマリ アクション">
                <select value={form.cta_primary_action} onChange={e => update('cta_primary_action', e.target.value)} className={INPUT}>
                  <option value="add_to_cart">カート追加</option>
                  <option value="view_detail">商品詳細へ</option>
                  <option value="custom_prompt">プロンプト送信</option>
                </select>
              </Field>
            </Row>
            {form.cta_primary_action === 'add_to_cart' && (
              <p className="text-xs text-gray-500">
                バリエーションが特定できない商品（在庫連携が無い等）では、自動で「商品ページを見る」に切り替わります。商品ページのURLも無い場合はボタンを表示しません。
              </p>
            )}
            {form.cta_primary_action === 'custom_prompt' && <Field label="送信プロンプト"><input value={form.cta_primary_prompt} onChange={e => update('cta_primary_prompt', e.target.value)} className={INPUT} /></Field>}
            <Row>
              <Field label="セカンダリ ラベル（空白なら非表示）"><input value={form.cta_secondary_label} onChange={e => update('cta_secondary_label', e.target.value)} className={INPUT} /></Field>
              <Field label="セカンダリ アクション">
                <select value={form.cta_secondary_action} onChange={e => update('cta_secondary_action', e.target.value)} className={INPUT}>
                  <option value="">-</option>
                  <option value="view_detail">商品詳細へ</option>
                  <option value="custom_prompt">プロンプト送信</option>
                </select>
              </Field>
            </Row>
          </Section>

          <Section title="デザイン">
            <Row>
              <Field label="背景タイプ">
                <select value={form.background_type} onChange={e => update('background_type', e.target.value)} className={INPUT}>
                  <option value="gradient">グラデ</option><option value="solid">単色</option><option value="image">画像</option>
                </select>
              </Field>
              <Field label="アクセント色"><ColorInput value={form.accent_color} onChange={v => update('accent_color', v)} /></Field>
            </Row>
            <Row>
              <Field label="背景プライマリ"><ColorInput value={form.background_primary_color} onChange={v => update('background_primary_color', v)} /></Field>
              <Field label="背景セカンダリ"><ColorInput value={form.background_secondary_color} onChange={v => update('background_secondary_color', v)} /></Field>
            </Row>
            {form.background_type === 'image' && <Field label="背景画像URL"><input value={form.background_image_url} onChange={e => update('background_image_url', e.target.value)} className={INPUT} /></Field>}
            <Row>
              <Field label="border-radius"><input value={form.border_radius} onChange={e => update('border_radius', e.target.value)} className={INPUT} placeholder="12px" /></Field>
              <Field label="テキスト色"><ColorInput value={form.text_color} onChange={v => update('text_color', v)} /></Field>
            </Row>
            <Field label="自由CSS（カード内）"><textarea value={form.custom_css} onChange={e => update('custom_css', e.target.value)} rows={3} className={`${TEXTAREA} font-mono`} /></Field>
          </Section>

          <Section title="動き">
            <Row>
              <Field label="入場アニメ">
                <select value={form.animation_in} onChange={e => update('animation_in', e.target.value)} className={INPUT}>
                  <option value="none">なし</option>
                  <option value="fade_in">フェードイン</option>
                  <option value="slide_up">スライドアップ</option>
                  <option value="zoom">ズーム</option>
                </select>
              </Field>
              <Field label="CTA ホバー">
                <select value={form.cta_hover_effect} onChange={e => update('cta_hover_effect', e.target.value)} className={INPUT}>
                  <option value="none">なし</option>
                  <option value="lift">浮き上がり</option>
                  <option value="glow">発光</option>
                  <option value="pulse">パルス</option>
                </select>
              </Field>
            </Row>
          </Section>
        </div>

        <div className={`${CARD} sticky top-3.5 h-fit p-3.5`}>
          <div className='text-primary mb-2.5 text-[13px] font-semibold'>👁️ プレビュー</div>
          <HeroPreviewCard form={form} />
        </div>
      </div>
    </div>
  )
}

function HeroPreviewCard({ form }) {
  let bgStyle = {}
  if (form.background_type === 'gradient') {
    bgStyle = { background: `linear-gradient(135deg,${form.background_primary_color},${form.background_secondary_color})` }
  } else if (form.background_type === 'image' && form.background_image_url) {
    bgStyle = { background: `url(${form.background_image_url}) center/cover` }
  } else {
    bgStyle = { background: form.background_primary_color }
  }
  return (
    <div style={{ borderRadius: form.border_radius, overflow: 'hidden', border: '1px solid #e5e7eb', background: '#fff' }}>
      <div style={{ position: 'relative', aspectRatio: '16/10', display: 'flex', alignItems: 'center', justifyContent: 'center', ...bgStyle }}>
        {form.hero_label && <div style={{ position: 'absolute', top: 8, left: 8, background: 'rgba(255,255,255,0.95)', color: '#000', padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{form.hero_label}</div>}
        <div style={{ color: form.text_color, fontSize: 12 }}>商品画像</div>
      </div>
      <div style={{ padding: 12, color: '#111' }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>商品名（プレビュー）</div>
        <div style={{ fontSize: 16, fontWeight: 700, marginTop: 4 }}>¥28,800</div>
        {form.recommend_reason_primary && <div style={{ background: 'rgba(99,102,241,0.08)', padding: 8, borderRadius: 6, fontSize: 11, marginTop: 6 }}>💡 {form.recommend_reason_primary}</div>}
        {form.urgency_message && <div style={{ background: '#fff3cd', color: '#664d03', padding: '6px 8px', borderRadius: 6, fontSize: 11, marginTop: 6 }}>⏰ {form.urgency_message}</div>}
        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
          <button style={{ flex: 1, padding: 8, background: form.accent_color, color: form.text_color, border: 'none', borderRadius: 6, fontWeight: 600 }}>{form.cta_primary_label}</button>
          {form.cta_secondary_label && <button style={{ flex: 1, padding: 8, background: 'transparent', color: '#000', border: '1px solid #000', borderRadius: 6 }}>{form.cta_secondary_label}</button>}
        </div>
      </div>
    </div>
  )
}

function BundlePresetEditor({ siteId, preset, onClose, onSaved }) {
  const { authHeaders } = useAuth()
  const isNew = !preset
  const [form, setForm] = useState(() => preset ? { ...preset } : {
    name: '', bundle_name: '', bundle_description: '',
    enabled: true, priority: 100,
    trigger_mode: 'llm_select', trigger_keywords: [], trigger_match_method: 'partial',
    category: '', items: [],
    discount_type: 'fixed', discount_value: 0, customizable: true,
    header_color: '#0d6efd', accent_color: '#198754', custom_css: '',
  })
  const [keywordInput, setKeywordInput] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }))
  const addItem = () => update('items', [...(form.items || []), { product_id: '', category_role: '', is_required: false }])
  const updateItem = (i, k, v) => update('items', form.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it))
  const removeItem = (i) => update('items', form.items.filter((_, idx) => idx !== i))

  const save = async () => {
    const errors = validateBundleForm(form)
    if (errors.length) { setError(errors.join(' / ')); return }
    setSaving(true); setError(null)
    try {
      const url = isNew ? `/api/sites/${siteId}/purchase-assist/bundle-presets` : `/api/sites/${siteId}/purchase-assist/bundle-presets/${preset.id}`
      const r = await fetch(url, { method: isNew ? 'POST' : 'PUT', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(form) })
      if (!r.ok) throw new Error(await presetErrorMessage(r))
      onSaved()
    } catch (e) { setError(e.message) } finally { setSaving(false) }
  }

  return (
    <div>
      <EditorHeader title={isNew ? '新規バンドル' : `編集: ${preset.name}`} onClose={onClose} onSave={save} saving={saving} error={error} />
      <Section title="基本情報">
        <Field label="プリセット名（運用識別） *"><input value={form.name} onChange={e => update('name', e.target.value)} className={INPUT} /></Field>
        <Field label="バンドル表示名 *"><input value={form.bundle_name} onChange={e => update('bundle_name', e.target.value)} className={INPUT} placeholder="キャンプ初心者セット" /></Field>
        <Field label="説明"><textarea value={form.bundle_description} onChange={e => update('bundle_description', e.target.value)} rows={2} className={TEXTAREA} /></Field>
        <Row>
          <Field label="カテゴリ"><input value={form.category} onChange={e => update('category', e.target.value)} className={INPUT} placeholder="キャンプ" /></Field>
          <Field label="優先度"><input type="number" value={form.priority} onChange={e => update('priority', Number(e.target.value))} className={INPUT} /></Field>
          <Field label="有効/無効">
            <select value={form.enabled ? '1' : '0'} onChange={e => update('enabled', e.target.value === '1')} className={INPUT}>
              <option value="1">有効</option><option value="0">無効</option>
            </select>
          </Field>
        </Row>
      </Section>

      <Section title="マッチング条件">
        <Field label="発火モード">
          <select value={form.trigger_mode} onChange={e => update('trigger_mode', e.target.value)} className={INPUT}>
            <option value="llm_select">LLM選択時のみ</option>
            <option value="force">🔥 強制発火</option>
            <option value="both">両方</option>
          </select>
        </Field>
        <KeywordInput label="トリガーキーワード" keywords={form.trigger_keywords || []} input={keywordInput} setInput={setKeywordInput} onChange={v => update('trigger_keywords', v)} placeholder="初心者、必要なもの" />
      </Section>

      <Section title="商品セット">
        <Button color={ButtonColor.SECONDARY} onClick={addItem}>＋ 商品を追加</Button>
        <div style={{ marginTop: 8 }}>
          {(form.items || []).map((it, i) => (
            <div key={i} className='bg-surface-secondary mb-2 rounded-lg p-2'>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px 60px', gap: 6 }}>
                <input value={it.product_id} onChange={e => updateItem(i, 'product_id', e.target.value)} placeholder="商品ID" className={INPUT} />
                <input value={it.category_role || ''} onChange={e => updateItem(i, 'category_role', e.target.value)} placeholder="役割（例: テント）" className={INPUT} />
                <label className='text-secondary flex items-center gap-1 text-[11px]'>
                  <input type="checkbox" checked={!!it.is_required} onChange={e => updateItem(i, 'is_required', e.target.checked)} /> 必須
                </label>
                <Button color={ButtonColor.CRITICAL} onClick={() => removeItem(i)}>削除</Button>
              </div>
              <ProductLookup siteId={siteId} productId={it.product_id} />
            </div>
          ))}
        </div>
      </Section>

      <Section title="価格設定">
        <Row>
          <Field label="割引タイプ">
            <select value={form.discount_type} onChange={e => update('discount_type', e.target.value)} className={INPUT}>
              <option value="fixed">固定額（円）</option>
              <option value="percent">パーセント</option>
            </select>
          </Field>
          <Field label="割引値"><input type="number" value={form.discount_value} onChange={e => update('discount_value', Number(e.target.value))} className={INPUT} /></Field>
          <Field label="内容変更可能">
            <label className='text-secondary text-[12px]'><input type="checkbox" checked={form.customizable} onChange={e => update('customizable', e.target.checked)} /> 許可</label>
          </Field>
        </Row>
      </Section>

      <Section title="デザイン">
        <Row>
          <Field label="ヘッダー色"><ColorInput value={form.header_color} onChange={v => update('header_color', v)} /></Field>
          <Field label="アクセント色"><ColorInput value={form.accent_color} onChange={v => update('accent_color', v)} /></Field>
        </Row>
        <Field label="自由CSS"><textarea value={form.custom_css} onChange={e => update('custom_css', e.target.value)} rows={3} className={`${TEXTAREA} font-mono`} /></Field>
      </Section>
    </div>
  )
}

function QuickChoicePresetEditor({ siteId, preset, onClose, onSaved }) {
  const { authHeaders } = useAuth()
  const isNew = !preset
  const [form, setForm] = useState(() => preset ? { ...preset } : {
    name: '', description: '', enabled: true, priority: 100,
    trigger_mode: 'llm_select', trigger_keywords: [], trigger_match_method: 'partial',
    category: '', question: '', question_purpose: '',
    choices: [{ id: 'a', label: '選択肢A', sub_label: '', icon: '💼', prompt: '' }, { id: 'b', label: '選択肢B', sub_label: '', icon: '🏔️', prompt: '' }],
    additional_inputs: [], accent_color: '#0d6efd', custom_css: '',
  })
  const [keywordInput, setKeywordInput] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }))
  const addChoice = () => update('choices', [...(form.choices || []), { id: `c${Date.now()}`, label: '', sub_label: '', icon: '', prompt: '' }])
  const updateChoice = (i, k, v) => update('choices', form.choices.map((c, idx) => idx === i ? { ...c, [k]: v } : c))
  const removeChoice = (i) => update('choices', form.choices.filter((_, idx) => idx !== i))

  const save = async () => {
    const errors = validateQuickChoiceForm(form)
    if (errors.length) { setError(errors.join(' / ')); return }
    setSaving(true); setError(null)
    try {
      const url = isNew ? `/api/sites/${siteId}/purchase-assist/quick-choice-presets` : `/api/sites/${siteId}/purchase-assist/quick-choice-presets/${preset.id}`
      const r = await fetch(url, { method: isNew ? 'POST' : 'PUT', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(form) })
      if (!r.ok) throw new Error(await presetErrorMessage(r))
      onSaved()
    } catch (e) { setError(e.message) } finally { setSaving(false) }
  }

  return (
    <div>
      <EditorHeader title={isNew ? '新規二択カード' : `編集: ${preset.name}`} onClose={onClose} onSave={save} saving={saving} error={error} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 14 }}>
        <div>
          <Section title="基本情報">
            <Field label="プリセット名 *"><input value={form.name} onChange={e => update('name', e.target.value)} className={INPUT} /></Field>
            <Row>
              <Field label="優先度"><input type="number" value={form.priority} onChange={e => update('priority', Number(e.target.value))} className={INPUT} /></Field>
              <Field label="有効/無効">
                <select value={form.enabled ? '1' : '0'} onChange={e => update('enabled', e.target.value === '1')} className={INPUT}>
                  <option value="1">有効</option><option value="0">無効</option>
                </select>
              </Field>
              <Field label="カテゴリ"><input value={form.category} onChange={e => update('category', e.target.value)} className={INPUT} /></Field>
            </Row>
          </Section>

          <Section title="マッチング条件">
            <Field label="発火モード">
              <select value={form.trigger_mode} onChange={e => update('trigger_mode', e.target.value)} className={INPUT}>
                <option value="llm_select">LLM選択時のみ</option>
                <option value="force">🔥 強制発火</option>
                <option value="both">両方</option>
              </select>
            </Field>
            <KeywordInput label="トリガーキーワード" keywords={form.trigger_keywords || []} input={keywordInput} setInput={setKeywordInput} onChange={v => update('trigger_keywords', v)} placeholder="悩んで、相談、選び方" />
          </Section>

          <Section title="質問内容">
            <Field label="質問文 *"><input value={form.question} onChange={e => update('question', e.target.value)} className={INPUT} placeholder="主な着用シーンはどちらですか？" /></Field>
            <Field label="質問意図"><input value={form.question_purpose} onChange={e => update('question_purpose', e.target.value)} className={INPUT} placeholder="シーンに応じて素材が変わるため" /></Field>
          </Section>

          <Section title="選択肢">
            <Button color={ButtonColor.SECONDARY} onClick={addChoice}>＋ 選択肢を追加</Button>
            <div style={{ marginTop: 8 }}>
              {(form.choices || []).map((c, i) => (
                <div key={i} className='border-secondary mb-2 rounded-lg border p-2.5'>
                  <Row>
                    <Field label="アイコン"><input value={c.icon} onChange={e => updateChoice(i, 'icon', e.target.value)} className={`${INPUT} w-20`} placeholder="💼" /></Field>
                    <Field label="ラベル"><input value={c.label} onChange={e => updateChoice(i, 'label', e.target.value)} className={INPUT} placeholder="通勤・ビジネス" /></Field>
                    <Field label="サブラベル"><input value={c.sub_label} onChange={e => updateChoice(i, 'sub_label', e.target.value)} className={INPUT} placeholder="きれいめ" /></Field>
                  </Row>
                  <div style={{ marginTop: 6 }}>
                    <Field label="送信プロンプト"><input value={c.prompt} onChange={e => updateChoice(i, 'prompt', e.target.value)} className={INPUT} placeholder="通勤向けの商品を見せて" /></Field>
                  </div>
                  <div className='mt-1.5'><Button color={ButtonColor.CRITICAL} onClick={() => removeChoice(i)}>この選択肢を削除</Button></div>
                </div>
              ))}
            </div>
          </Section>

          <Section title="デザイン">
            <Field label="アクセント色"><ColorInput value={form.accent_color} onChange={v => update('accent_color', v)} /></Field>
            <Field label="自由CSS"><textarea value={form.custom_css} onChange={e => update('custom_css', e.target.value)} rows={3} className={`${TEXTAREA} font-mono`} /></Field>
          </Section>
        </div>

        <div className={`${CARD} sticky top-3.5 h-fit p-3.5`}>
          <div className='text-primary mb-2.5 text-[13px] font-semibold'>👁️ プレビュー</div>
          <div style={{ padding: 12, background: '#fff', color: '#111', borderRadius: 8, border: '1px solid #e5e7eb' }}>
            {form.question && <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{form.question}</div>}
            {form.question_purpose && <div style={{ fontSize: 11, color: '#666', marginBottom: 10 }}>{form.question_purpose}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(form.choices?.length || 1, 3)},1fr)`, gap: 8 }}>
              {(form.choices || []).map((c, i) => (
                <div key={i} style={{ padding: '14px 8px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fafafa', textAlign: 'center' }}>
                  <div style={{ fontSize: 24, marginBottom: 6 }}>{c.icon}</div>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{c.label}</div>
                  {c.sub_label && <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>{c.sub_label}</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================
// 共通UIヘルパー
// ============================================================

function EditorHeader({ title, onClose, onSave, saving, error }) {
  return (
    <div>
      <div className='mb-3.5 flex items-center gap-2.5'>
        <Button color={ButtonColor.SECONDARY} onClick={onClose}>← 戻る</Button>
        <h2 className='text-primary m-0 flex-1 text-base font-bold'>{title}</h2>
        <Button color={ButtonColor.PRIMARY} onClick={onSave} disabled={saving}>{saving ? '保存中...' : '💾 保存'}</Button>
      </div>
      {error && <div className='bg-surface-critical text-critical mb-3 rounded-lg p-2.5 text-xs'>⚠️ {error}</div>}
    </div>
  )
}

function KeywordInput({ label, keywords, input, setInput, onChange, placeholder }) {
  const add = () => {
    if (input.trim()) {
      onChange([...keywords, input.trim()])
      setInput('')
    }
  }
  const remove = (i) => onChange(keywords.filter((_, idx) => idx !== i))
  return (
    <Field label={label}>
      <div style={{ display: 'flex', gap: 6 }}>
        <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => isEnterWithoutComposing(e) && add()} className={INPUT} placeholder={placeholder} />
        <Button color={ButtonColor.SECONDARY} onClick={add}>追加</Button>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
        {keywords.map((k, i) => (
          <span key={i} className='bg-surface-emphasis text-emphasis rounded-[10px] px-2 py-0.5 text-[11px]'>
            {k} <span onClick={() => remove(i)} className='ml-1 cursor-pointer'>×</span>
          </span>
        ))}
      </div>
    </Field>
  )
}

function ProductLookup({ siteId, productId }) {
  const { authHeaders } = useAuth()
  const [info, setInfo] = useState(null)
  const [error, setError] = useState(null)
  useEffect(() => {
    if (!productId || !productId.trim()) { setInfo(null); setError(null); return }
    let live = true
    const t = setTimeout(() => {
      fetch(`/api/sites/${siteId}/purchase-assist/products/lookup?product_id=${encodeURIComponent(productId.trim())}`, { headers: authHeaders() })
        .then(async r => {
          if (!live) return
          if (r.ok) { setInfo(await r.json()); setError(null) }
          else { setInfo(null); setError(r.status === 404 ? '該当なし' : `HTTP ${r.status}`) }
        })
        .catch(e => { if (live) { setInfo(null); setError(e.message) } })
    }, 300)
    return () => { live = false; clearTimeout(t) }
  }, [siteId, productId])
  if (!productId) return null
  if (error) return <div className='text-critical mt-1 text-[11px]'>⚠️ {error}</div>
  if (!info) return <div className='text-tertiary mt-1 text-[11px]'>検索中...</div>
  return (
    <div className='bg-surface-success mt-1 flex items-center gap-1.5 rounded p-1.5 text-[11px]'>
      {info.image_url && <img src={info.image_url} alt="" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 3 }} />}
      <div className='min-w-0 flex-1'>
        <div className='text-primary truncate font-semibold'>✓ {info.name}</div>
        <div className='text-secondary'>¥{info.price.toLocaleString()}{info.category && ' ・ ' + info.category}</div>
      </div>
    </div>
  )
}

function CssField({ value, onChange }) {
  return (
    <Field label="このコンポーネントだけに適用するCSS">
      <textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={4} className={`${TEXTAREA} font-mono`} placeholder="/* 例 */" />
    </Field>
  )
}

function Section({ title, children }) {
  return (
    <div className='mb-[18px]'>
      <h3 className='text-primary mb-2 pl-2 text-[13px] font-semibold' style={{ borderLeft: '3px solid var(--color-fill-brand)' }}>{title}</h3>
      <div className='flex flex-col gap-2.5'>{children}</div>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div className='flex-1'>
      <label className={`${LABEL} mb-1 block`}>{label}</label>
      {children}
    </div>
  )
}

function Row({ children }) {
  return <div style={{ display: 'flex', gap: 10 }}>{children}</div>
}

function ColorInput({ value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <input type="color" value={value || '#000000'} onChange={e => onChange(e.target.value)} style={{ width: 32, height: 24, border: 'none', cursor: 'pointer' }} />
      <input type="text" value={value || ''} onChange={e => onChange(e.target.value)} className={`${INPUT} font-mono`} />
    </div>
  )
}

// ============================================================
// ヘルプオーバーレイ（コンポーネント選択ガイド）
// ============================================================

const HELP_GROUPS = [
  {
    title: 'A. 商品発見フェーズ向け',
    color: '#6366f1',
    items: [
      { num: '①', name: '商品カードギャラリー', subtitle: '横スクロール / Netflix風', desc: '複数商品を横スクロールで提示。視覚的に商品を流し見できる。スマホ最適。' },
      { num: '②', name: '大きな主役カード（Hero Card）', subtitle: '1商品の強プッシュ', desc: '「これがイチオシ！」という1商品を大きく見せる。背景画像 + 商品名 + 推薦理由 + CTAボタン。新商品・PB商品プッシュに有効。' },
    ],
  },
  {
    title: 'B. 比較検討フェーズ向け',
    color: '#8b5cf6',
    items: [
      { num: '③', name: '横並び比較テーブル', subtitle: '2-3商品の並列比較', desc: 'スペック・価格・特徴で並列比較。差分セルをハイライトすることで違いが一目で分かる。' },
      { num: '④', name: '比較レーダーチャート', subtitle: '多軸スコア可視化', desc: '価格・品質・デザイン・機能等を5軸で可視化。直感的な比較に有効。' },
      { num: '⑤', name: '「あなたへの推薦理由」付きカード', subtitle: 'パーソナライズ', desc: '各商品に「過去購入履歴から」「レビューで○○と高評価」等のパーソナライズ理由を表示。' },
    ],
  },
  {
    title: 'C. 意思決定支援向け',
    color: '#ec4899',
    items: [
      { num: '⑥', name: '質問形式の絞り込みカード', subtitle: '2択で会話を進める', desc: '「どちらが優先ですか？」を画像付き2択カードで選択させる。タップで会話を進める。' },
      { num: '⑦', name: '価格帯スライダー + 動的フィルタ', subtitle: 'インタラクティブ絞込', desc: '予算に応じて商品候補がリアルタイムに変わるインタラクティブUI。' },
      { num: '⑧', name: 'レビューハイライトカード', subtitle: '良い点・気になる点', desc: 'レビューの抜粋を「良い点」「気になる点」で構造化表示。AIがレビューを要約して提示。' },
    ],
  },
  {
    title: 'D. 体験を変える応用パターン',
    color: '#10b981',
    items: [
      { num: '⑩', name: 'シーン別おすすめバンドル', subtitle: 'セット販売・クロスセル', desc: '「キャンプ用品セット」のように複数商品をバンドル提案。「3点セットで¥15,000」等。' },
    ],
  },
]

const HELP_RULES = [
  { trigger: '「おすすめは？」', target: '① 商品ギャラリー', color: '#6366f1' },
  { trigger: '特定の1商品を求めている', target: '② Heroカード', color: '#8b5cf6' },
  { trigger: '2-3商品を比較している', target: '③ 比較テーブル', color: '#ec4899' },
  { trigger: '意図が曖昧で絞り込みが必要', target: '⑥ 二択カード', color: '#f59e0b' },
  { trigger: '用途が明確でセット買いが有効', target: '⑩ バンドル', color: '#10b981' },
  { trigger: 'スコア比較したい（性能・コスパ等）', target: '③ 比較テーブル / ④ レーダーチャート', color: '#3b82f6' },
]

const HELP_USECASES = [
  { items: ['① 商品ギャラリー', '③ 比較テーブル'], label: '最頻出ユースケース', color: '#6366f1' },
  { items: ['② Heroカード', '⑥ 二択カード'], label: 'パーソナライズ強化', color: '#8b5cf6' },
  { items: ['⑩ バンドル', '⑧ レビューハイライト'], label: 'クロスセル・意思決定支援', color: '#ec4899' },
  { items: ['⑦ フィルタスライダー', '④ レーダーチャート'], label: '高度なインタラクション', color: '#f59e0b' },
  { items: ['⑤ パーソナライズ'], label: 'Reco-Mediaと連動', color: '#10b981' },
]

function HelpOverlay({ onClose }) {
  return (
    // 2026-10-03 修正：body へ portal で出し、ヘッダーより前面に置く（SMART_HUB_DEV-142）。
    // 修正前は zIndex: 1000 で、固定ヘッダー（z-header 1050）の裏に上端が隠れていた
    <ModalOverlay
      onClose={onClose}
      className='z-modal'
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 20px',
        overflowY: 'auto',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        className='bg-surface border-secondary shadow-area rounded-2xl border p-7'
        style={{ width: '100%', maxWidth: 980 }}
      >
        {/* ヘッダー */}
        <div className='mb-2 flex items-center gap-2.5'>
          <h2 className='text-primary m-0 flex-1 text-lg font-bold'>📖 コンポーネント選択ガイド</h2>
          <button onClick={onClose} className='bg-surface-secondary text-secondary hover:bg-surface-hover flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-base'>×</button>
        </div>
        <p className='text-secondary mb-5 text-xs leading-relaxed'>
          AI が会話文脈に応じて最適なリッチUIを自動選択するための判断基準です。<br />
          フェーズ別の「使い分け」と、トリガー別の「選択ガイドライン」を確認できます。
        </p>

        {/* セクション1: 10種類の表示パターン */}
        <h3 className='text-primary mt-0 mb-3.5 pl-2.5 text-sm font-bold' style={{ borderLeft: '3px solid var(--color-fill-brand)' }}>10種類の表示パターン（フェーズ別）</h3>
        {HELP_GROUPS.map((g, gi) => (
          <div key={gi} className='mb-4'>
            <div
              className='mb-2 rounded-lg px-3 py-1.5 text-xs font-bold'
              style={{ color: g.color, background: `${g.color}15`, borderLeft: `3px solid ${g.color}` }}
            >{g.title}</div>
            <div className='grid grid-cols-2 gap-2'>
              {g.items.map((it, ii) => (
                <div key={ii} className='bg-surface-secondary rounded-lg p-3' style={{ border: `1px solid ${g.color}33` }}>
                  <div className='mb-1 flex items-center gap-1.5'>
                    <span
                      className='inline-flex h-[22px] w-[22px] items-center justify-center rounded text-base font-bold'
                      style={{ color: g.color, background: `${g.color}22` }}
                    >{it.num}</span>
                    <span className='text-primary text-[13px] font-semibold'>{it.name}</span>
                  </div>
                  <div className='mb-1.5 text-[10px] font-semibold' style={{ color: g.color }}>{it.subtitle}</div>
                  <div className='text-secondary text-[11px] leading-normal'>{it.desc}</div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* セクション2: 選択ガイドライン */}
        <h3 className='text-primary mt-6 mb-3 pl-2.5 text-sm font-bold' style={{ borderLeft: '3px solid var(--color-fill-brand)' }}>📐 LLMコンポーネント選択ガイドライン</h3>
        <p className='text-tertiary mb-3 text-[11px] leading-relaxed'>
          AI（Anthropic Claude Tool Use）に「いつどのコンポーネントを使うか」を理解させてあります。以下のトリガー → 推奨コンポーネントの対応表に沿って自動選択されます。
        </p>
        <div className='bg-surface-secondary border-secondary overflow-hidden rounded-lg border'>
          {HELP_RULES.map((r, i) => (
            <div key={i} className={`grid grid-cols-[1fr_auto_1fr] items-center gap-2.5 px-3.5 py-2.5 ${i === 0 ? '' : 'border-t border-secondary'}`}>
              <div className='text-secondary text-xs'>💬 {r.trigger}</div>
              <div className='text-tertiary text-sm'>→</div>
              <div className='text-xs font-semibold' style={{ color: r.color }}>{r.target}</div>
            </div>
          ))}
        </div>

        {/* セクション3: ユースケース別マッピング */}
        <h3 className='text-primary mt-6 mb-3 pl-2.5 text-sm font-bold' style={{ borderLeft: '3px solid var(--color-fill-brand)' }}>🎯 ユースケース別マッピング</h3>
        <div className='grid grid-cols-2 gap-2.5'>
          {HELP_USECASES.map((uc, i) => (
            <div key={i} className='bg-surface-secondary flex items-center justify-between gap-2.5 rounded-lg p-3' style={{ border: `1px solid ${uc.color}33` }}>
              <div className='flex flex-wrap gap-1'>
                {uc.items.map((item, ii) => (
                  <span key={ii} className='rounded px-2 py-0.5 text-[11px] font-semibold' style={{ background: `${uc.color}22`, color: uc.color }}>{item}</span>
                ))}
              </div>
              <div className='text-secondary text-right text-[11px]'>{uc.label}</div>
            </div>
          ))}
        </div>

        {/* フッター */}
        <div className='bg-surface-emphasis text-secondary mt-5 rounded-lg p-3 text-[11px] leading-relaxed'>
          💡 <strong className='text-emphasis'>運用ヒント</strong>: コンポーネントを「使う」にするだけで AI が自動選択します。
          特定商品を強くプッシュしたい場合は <strong>Heroカード管理</strong>・<strong>バンドル管理</strong>・<strong>二択カード管理</strong> でプリセットを登録してください。
          キーワードヒット時は LLM 呼び出し前に強制発火し、確実に表示されます。
        </div>

        <div className='mt-4 text-right'>
          <Button color={ButtonColor.PRIMARY} onClick={onClose}>閉じる</Button>
        </div>
      </div>
    </ModalOverlay>
  )
}
